import React, { useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, Loader2, Search, ExternalLink, Syringe } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Dashboard-side "Suggest Vaccine" dialog. Pick a patient → we fetch their
 * due/overdue vaccines from /api/vaccines/suggest/{pid} and show the list.
 * The doctor opens the patient page and accepts the suggestions there via
 * the same button in VaccinesPlanner (which appends the rows to the visit).
 */
export default function SuggestVaccineDialog({ trigger }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);   // {patient_id, name, mobile}
  const [suggestions, setSuggestions] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);

  const runSearch = async (val) => {
    setQ(val);
    if ((val || "").trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const params = new URLSearchParams();
      params.set("q", val);
      const r = await api.get(`/patients/search?${params.toString()}`);
      setResults(r.data.results || []);
    } catch (e) { console.debug("[silent]", e); }
    setSearching(false);
  };

  const pickPatient = async (p) => {
    setPicked(p);
    setLoading(true);
    setSuggestions(null);
    try {
      const r = await api.get(`/vaccines/suggest/${p.patient_id}`);
      setSuggestions(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch suggestions");
      setSuggestions({ suggestions: [] });
    }
    setLoading(false);
  };

  const openPatient = () => {
    if (!picked?.patient_id) return;
    setOpen(false);
    nav(`/patient/${picked.patient_id}?suggestVaccines=1`);
  };

  const reset = () => {
    setPicked(null); setSuggestions(null); setQ(""); setResults([]);
  };

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true), "data-testid": "dashboard-suggest-vaccine-btn" })
        : (
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setOpen(true)} data-testid="dashboard-suggest-vaccine-btn">
            <Sparkles size={14} className="text-indigo-600" /> Suggest Vaccine
          </Button>
        )}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-lg" data-testid="dashboard-suggest-vaccine-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles size={16} className="text-indigo-600" /> Suggest a vaccine
            </DialogTitle>
          </DialogHeader>
          {!picked && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600">Search a patient by name, mobile or ID. We&rsquo;ll list the vaccines that are due or overdue based on their age &amp; risk profile.</div>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-3 text-slate-400" />
                <Input
                  autoFocus
                  value={q}
                  onChange={(e) => runSearch(e.target.value)}
                  placeholder="Search patient — name, mobile or ID"
                  className="pl-8"
                  data-testid="suggest-vaccine-search"
                />
              </div>
              <div className="max-h-64 overflow-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                {searching && <div className="flex items-center gap-2 px-3 py-3 text-[11px] text-slate-500"><Loader2 size={12} className="animate-spin" /> Searching…</div>}
                {!searching && results.length === 0 && q.length >= 2 && (
                  <div className="px-3 py-3 text-[11px] text-slate-500">No patients found.</div>
                )}
                {!searching && q.length < 2 && (
                  <div className="px-3 py-3 text-[11px] text-slate-500">Type at least 2 characters to search.</div>
                )}
                {results.map((p) => (
                  <button
                    key={p.patient_id}
                    onClick={() => pickPatient(p)}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 flex items-center justify-between gap-2"
                    data-testid={`suggest-vaccine-pick-${p.patient_id}`}
                  >
                    <div>
                      <div className="text-sm font-medium text-slate-800">{p.name}</div>
                      <div className="text-[11px] text-slate-500">{p.mobile} · {p.patient_id}{p.dob && ` · DOB ${p.dob}`}</div>
                    </div>
                    <ExternalLink size={12} className="text-slate-400" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {picked && (
            <div className="space-y-3">
              <div className="text-xs text-slate-700 border border-slate-200 rounded-md p-2 bg-slate-50 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-slate-800">{picked.name}</div>
                  <div className="text-[11px] text-slate-500">{picked.mobile} · {picked.patient_id}</div>
                </div>
                <button className="text-[11px] text-indigo-700 hover:underline" onClick={reset} data-testid="suggest-vaccine-reset">Change patient</button>
              </div>
              {loading && (
                <div className="flex items-center gap-2 text-[11px] text-slate-500"><Loader2 size={12} className="animate-spin" /> Analysing schedule…</div>
              )}
              {!loading && suggestions && (
                <>
                  {suggestions.suggestions?.length === 0 ? (
                    <div className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                      No vaccines are due — this patient appears up to date.
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-64 overflow-auto" data-testid="suggest-vaccine-list">
                      {suggestions.suggestions.map((s, i) => (
                        <div key={i} className="px-3 py-2 flex items-start justify-between gap-2" data-testid={`suggest-vaccine-item-${i}`}>
                          <div>
                            <div className="text-sm text-slate-800 flex items-center gap-1.5"><Syringe size={12} className="text-pink-600" /> {s.name}</div>
                            <div className="text-[11px] text-slate-500">{s.reason} · appt {s.date}</div>
                          </div>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${s.status === "overdue" ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                            {s.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={reset} data-testid="suggest-vaccine-back">Back</Button>
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1.5" onClick={openPatient} data-testid="suggest-vaccine-open-patient">
                      Open patient <ExternalLink size={12} />
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
